import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { PreviewData } from '../types/preview';
import { PUBLIC_PLANS } from '../lib/pricing';
import BusinessInfoSidebar from './BusinessInfoSidebar';
import PricingSection from './PricingSection';

const PreviewPage: React.FC = () => {
  const { previewId } = useParams<{ previewId: string }>();
  const navigate = useNavigate();

  const { data: previewData, isLoading, error } = useQuery<PreviewData>({
    queryKey: ['preview', previewId],
    queryFn: async () => {
      const response = await axios.get(`/api/preview/${previewId}`);
      return response.data;
    },
    enabled: !!previewId,
  });

  const handleClaimWebsite = () => {
    // fnd-k1d.7: this used to navigate to /onboarding/:previewToken, the
    // Netlify-era self-serve checkout that has been deleted (an
    // unauthenticated endpoint that minted tokens for any submitted email).
    // There is no self-serve signup any more — real conversions come through
    // the invite-claim link a prospect receives, so the only sensible
    // in-app destination left is the login page.
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-zinc-600 text-sm sm:text-base">Loading your website preview...</p>
          <p className="mt-2 text-zinc-500 text-xs">This may take a few moments</p>
        </div>
      </div>
    );
  }

  if (error || !previewData) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
          <div className="text-red-500 text-5xl sm:text-6xl mb-4">⚠️</div>
          <h1 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-2">Preview Not Found</h1>
          <p className="text-zinc-600 mb-6 text-sm sm:text-base">
            This preview URL is invalid or has expired. Please check the link and try again.
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-accent text-white px-6 py-3 rounded-lg hover:bg-accent-dark transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <h1 className="text-xl sm:text-2xl font-bold text-zinc-900">
              Your Website Preview
            </h1>
            <div className="text-sm text-zinc-500 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
              </svg>
              <span className="truncate">Preview for {previewData.businessInfo.name}</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 lg:gap-8">
          {/* Main Content - Website Preview */}
          <div className="xl:col-span-3 order-2 xl:order-1">
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="bg-zinc-100 px-4 py-3 border-b flex items-center">
                <div className="flex space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                </div>
                <div className="ml-4 text-sm text-zinc-600 font-mono truncate">
                  {previewData.businessInfo.name.toLowerCase().replace(/\s+/g, '')}.com
                </div>
              </div>
              
              {/* Website Content Preview — there is no live site behind this
                  legacy preview-token flow (no slug/URL in the lead record),
                  so this renders the generated copy directly instead of an
                  iframe pointed at nothing. See fnd-lly.11: the previous
                  iframe src was a backend route that was itself hardcoded
                  placeholder scaffolding removed in fnd-lly.5. */}
              <div className="bg-white p-6 sm:p-10">
                <div className="max-w-2xl">
                  <h2 className="text-2xl sm:text-3xl font-bold text-zinc-900">
                    {previewData.websiteContent.hero.headline}
                  </h2>
                  <p className="mt-3 text-zinc-600">
                    {previewData.websiteContent.hero.subheadline}
                  </p>
                  <button
                    onClick={handleClaimWebsite}
                    className="mt-6 inline-flex items-center px-5 py-2.5 rounded-lg bg-accent text-white font-medium hover:bg-accent-dark transition-colors"
                  >
                    {previewData.websiteContent.hero.ctaText}
                  </button>
                </div>

                {previewData.websiteContent.about && (
                  <div className="mt-10 max-w-2xl space-y-3 text-sm text-zinc-600">
                    <p>{previewData.websiteContent.about.paragraph_1}</p>
                    <p>{previewData.websiteContent.about.paragraph_2}</p>
                  </div>
                )}

                {previewData.websiteContent.services?.service_list?.length > 0 && (
                  <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {previewData.websiteContent.services.service_list.map((service) => (
                      <div key={service.name} className="rounded-lg border border-zinc-200 p-4">
                        <h3 className="font-semibold text-zinc-900">{service.name}</h3>
                        <p className="mt-1 text-sm text-zinc-600">{service.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="xl:col-span-1 order-1 xl:order-2 space-y-6">
            {/* Business Information */}
            <BusinessInfoSidebar businessInfo={previewData.businessInfo} />
            
            {/* Pricing and CTA — public ladder only (Managed/Growth). Site
                ($99) is a real, billable plan (see packages/pricing) but is
                not offered on this cold, unauthenticated route. */}
            <PricingSection
              onSelectPlan={() => handleClaimWebsite()}
              currentPlan={null}
              plans={PUBLIC_PLANS}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewPage;